import { useMemo, useState } from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { DataGridPro, GridCellParams, GridColDef } from '@mui/x-data-grid-pro'

import DialogWithTitle from '../../../components/dialog-with-title'
import { getFormattedDateTime } from '../../../utils/helpers'
import useClipboard from '../../../utils/hooks/useClipboard'
import useStyles from '../index.styles'

const SimpleCellValue = ({ value }: { value: string | number | boolean | object | null }) => {
  const classes = useStyles
  const { clickToCopy } = useClipboard()
  const [open, setOpen] = useState<boolean>(false)

  if (value && typeof value === 'object') {
    const jsonStr = JSON.stringify(value, null, 2)
    return (
      <>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Raw JSON Data
        </Button>
        <DialogWithTitle open={open} setOpen={setOpen} title="Raw JSON Data">
          <Box
            sx={{
              marginX: 3,
            }}
          >
            <pre>{jsonStr}</pre>
          </Box>
          <Button onClick={() => clickToCopy(jsonStr)} sx={{ position: 'absolute', right: '56px', top: '18px' }}>
            Copy
          </Button>
        </DialogWithTitle>
      </>
    )
  }
  return <Typography sx={classes.truncatedText}>{typeof value !== 'string' ? value?.toString() : value}</Typography>
}

const DeviceChannelsTable = ({ device, searchText }: { device: IDeviceAPIObject; searchText: string }) => {
  const { currentValues } = device

  const columns: GridColDef<DeviceChannelTableRow>[] = [
    {
      field: 'channel',
      headerName: 'Channel Name',
      flex: 1,
      renderCell: (cellParams: GridCellParams<DeviceChannelTableRow, string>) => <SimpleCellValue value={cellParams.row.channel} />,
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 1,
      renderCell: (cellParams: GridCellParams<DeviceChannelTableRow, string>) => <SimpleCellValue value={cellParams.row.value} />,
    },
    {
      field: 'timestamp',
      headerName: 'Date & Time',
      flex: 1,
      renderCell: (cellParams: GridCellParams<DeviceChannelTableRow, string>) => {
        const { timestamp } = cellParams.row

        if (timestamp && new Date(timestamp).valueOf() > 0)
          return <SimpleCellValue value={`${getFormattedDateTime(timestamp, 'UTC', "LLLL do yyyy, h:mm aaa 'UTC'")}`} />
        return null
      },
    },
  ] satisfies GridColDef<DeviceChannelTableRow>[]

  const rows = useMemo(() => {
    let channelList = Object.keys(currentValues).map((channel) => ({
      id: channel,
      channel,
      value: currentValues[channel].value,
      timestamp: currentValues[channel].ts,
    }))
    if (searchText) channelList = channelList.filter((row) => row.channel.toLowerCase().includes(searchText.toLowerCase()))
    return channelList
  }, [currentValues, searchText])

  return (
    <Grid
      sx={{
        flexDirection: 'column',
      }}
    >
      <DataGridPro
        columns={columns}
        rows={rows}
        rowHeight={56}
        pagination={true}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        disableRowSelectionOnClick={true}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            border: 0,
          },
        }}
      />
    </Grid>
  )
}

export default DeviceChannelsTable
